import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import { withStyles } from "@mui/styles";

import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MapCard from "./MapCard";
import MatchPlayerBox from "./MatchPlayerBox";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckIcon from "@mui/icons-material/Check";
import CircularProgress from "@mui/material/CircularProgress";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ComputerIcon from "@mui/icons-material/Computer";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import GameDurationClock from "./GameDurationClock";
import ReportDialog from "./ReportDialog";
import MatchScoreboardSection from "./MatchScoreboardSection";
import MatchAnalysisSection from "./MatchAnalysisSection";
import {
  getGcEventType,
  getGcEventTimestampUnix,
  normalizeUnixSeconds,
} from "../../universal/gcEvents";
import { getMapMetaById } from "../../constants/mapMeta";
import { canShowMatchServerIp } from "./matchPageServerIp";
import { notifyIfCannotSendFriendRequest } from "../../utils/friendRequests";
import { getMatchServerLocation } from "./matchPageServerLocation";
import { getMatchPageViewState } from "./matchPageViewState";
import {
  canShowMatchDemoDownload,
  buildMatchDemoDownloadUrl,
} from "./matchDemoDownload";
import { shouldShowMatchPageBackToLobby } from "./matchPageNavigation";
import {
  DEFAULT_MATCH_PLAYER_CARD_VIEW,
  MATCH_PLAYER_CARD_VIEW_OPTIONS,
  readMatchPlayerCardView,
  writeMatchPlayerCardView,
} from "./matchPlayerCardViewMode";
import { getViewerLeaverPenaltyAppealContext } from "../support/leaverPenaltyAppeal";
import utils from "../../universal/utils";
import {
  consumeMatchPageLandedSoundForMatch,
  playMatchPageLandedSound,
} from "../../universal/matchNotificationSounds";

const FALLBACK_JOIN_WINDOW_SECONDS = 3 * 60;

function formatCountdownMmSs(totalSeconds) {
  const seconds = Math.max(0, Number.isFinite(totalSeconds) ? totalSeconds : 0);
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function getResolvedWinnerTeam(matchData) {
  const directWinner = matchData?.winnerTeam;
  if (directWinner === "team1" || directWinner === "team2") {
    return directWinner;
  }

  const recordedGameEvents = Array.isArray(matchData?.recordedGameEvents)
    ? matchData.recordedGameEvents
    : [];
  const seriesEndEvent = recordedGameEvents.find(
    (event) => getGcEventType(event) === "series_end",
  );
  const payload =
    seriesEndEvent?.payload?.payload || seriesEndEvent?.payload || {};
  const winnerTeam = payload?.body?.winner?.team;
  return winnerTeam === "team1" || winnerTeam === "team2" ? winnerTeam : null;
}

function getTeamDisplayName(player, fallbackLabel) {
  if (!player) return fallbackLabel;
  return `Team_${player.nickname || player.name}`;
}

function getSurrenderAnnotation(matchData) {
  if (matchData?.endedBy !== "surrender") {
    return null;
  }
  return matchData?.endedByLabel || "Surrender";
}

const styles = (theme) => ({
  root: {
    paddingTop: 16,
    paddingBottom: 16,
    marginTop: 0,

    marginLeft: "auto",
    marginRight: "auto",

    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  homepageRoot: {
    minHeight: "100vh",
    marginTop: 0,
    padding: "1.5rem 0 2.5rem",
    background:
      "radial-gradient(circle at 16% 0%, rgba(232, 163, 61,0.18), transparent 24%), radial-gradient(circle at 82% 14%, rgba(255,255,255,0.08), transparent 18%), radial-gradient(circle at 55% 30%, rgba(232, 163, 61,0.06), transparent 24%), linear-gradient(180deg, #070707 0%, #0d0d0d 42%, #111111 100%)",
  },
  lobbyFrame: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "1.5rem",
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
  },
  waitingText: {
    width: "100%",
    textAlign: "center",
    fontSize: "1.2rem",
    fontWeight: 525,
    color: "#ffffff",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 200px)",
    padding: "2rem",
  },
  loadingCard: {
    background:
      "linear-gradient(180deg, rgba(18,18,18,0.96) 0%, rgba(18,18,18,0.84) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: "3rem 2.5rem",
    maxWidth: 400,
    width: "100%",
    textAlign: "center",
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.34)",
    backdropFilter: "blur(16px)",
  },
  loadingSpinner: {
    color: "#E8A33D",
    marginBottom: "1.5rem",
  },
  loadingTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "0.5rem",
  },
  loadingText: {
    fontSize: "0.95rem",
    color: "#b5b1b3",
    lineHeight: 1.5,
  },
  teamLayout: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  containerTeamNameLayout: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "0.6rem",
    width: "100%",
    marginBottom: "0.2rem",
  },
  teamLeftName: {
    textAlign: "left",
    fontSize: "1.2rem",
    fontWeight: 525,
  },
  teamRightName: {
    width: "100%",
    textAlign: "end",
    fontSize: "1.2rem",
    fontWeight: 525,
  },
  trophyIconWinnerLabel: {
    color: "#FDD808",
    fontSize: "1.5rem",
  },
  teamContainer: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    background:
      "linear-gradient(180deg, rgba(18,18,18,0.94) 0%, rgba(18,18,18,0.82) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    boxSizing: "border-box",
    width: "26rem",
    maxWidth: "100%",
    rowGap: "1rem",
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.34)",
    backdropFilter: "blur(14px)",
  },
  teamAnimatedLine: {
    position: "absolute",
    "& path": {
      stroke: "#b5b1b3",
      strokeWidth: 1,
    },
  },
  teamAnimatedLineEnergy: {
    position: "absolute",
    pointerEvents: "none",
    "& path": {
      stroke: "#E8A33D",
      strokeWidth: 4,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeDasharray: "120 1000",
      strokeDashoffset: 1000,
      opacity: 0,
      filter: `
        drop-shadow(0 0 8px rgba(232, 163, 61,0.9))
        drop-shadow(0 0 14px rgba(232, 163, 61,0.6))
        drop-shadow(0 0 22px rgba(232, 163, 61,0.4))
      `,
      transition: "opacity 0.3s ease-in-out",
    },
  },
  teamAnimatedLineEnergyActive: {
    "& path": {
      opacity: 1,
      animation:
        "$energyTravel 3.5s linear infinite, $glowPulse 1.8s ease-in-out infinite",
    },
  },
  upLeftAnimatedLine: {
    top: "4rem",
    left: "calc(100% - 1.6rem)",
    transform: "rotateX(180deg)",
  },
  downLeftAnimatedLine: {
    bottom: "4rem",
    left: "calc(100% - 1.6rem)",
  },
  upRightAnimatedLine: {
    top: "4rem",
    right: "calc(100% - 1.6rem)",
    transform: "rotateX(180deg) rotateY(180deg)",
  },
  downRightAnimatedLine: {
    bottom: "4rem",
    right: "calc(100% - 1.6rem)",
    transform: "rotateY(180deg)",
  },
  iconDuration: {
    width: "1.8rem",
    height: "1.8rem",
    marginRight: "0.3rem",
    color: "#ffffff",
  },
  queueTimerCont: {
    borderRadius: 8,
    fontFamily: "Roboto",
    fontWeight: 525,
    fontSize: 20,
    width: 75,
    left: 144,
    top: 54,
    position: "absolute",
    display: "flex",
    alignItems: "center",
  },
  clockTime: {
    color: "#ffffff",
    fontSize: "24px",
    fontWeight: 600,
    textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    letterSpacing: "0.1em",
  },
  clockTextStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    lineHeight: 1.1,
  },
  joinCountdownLabel: {
    color: "#b5b1b3",
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: "0.15rem",
  },
  joinCountdownHint: {
    boxSizing: "border-box",
    width: "100%",
    padding: "0.4rem 1.5rem 0",
    display: "flex",
    justifyContent: "flex-end",
  },
  joinCountdownHintText: {
    color: "#ffffff",
    fontSize: "0.85rem",
    fontWeight: 500,
    textAlign: "right",
  },
  joinCountdownHintTime: {
    color: "#E8A33D",
    fontWeight: 800,
    letterSpacing: "0.06em",
  },
  labelMatchStatus: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  labelMatchStatusText: {
    position: "relative",
    padding: "0.3rem 1rem",
    borderRadius: "20px",
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#ffffff",
    textAlign: "center",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
    fontFamily: "Roboto, sans-serif",
    transition: "all 0.3s ease",
  },
  labelMatchStatusTextLive: {
    background: "#38B000",
    animation: "$liveStatusPulse 3s ease-in-out infinite",
  },
  labelMatchStatusTextFinished: {
    background: "#6C757D",
  },
  labelMatchStatusTextPending: {
    background: "#E8A33D",
  },
  matchEndAnnotation: {
    marginTop: "0.35rem",
    color: "#b5b1b3",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textAlign: "center",
  },
  "@keyframes liveStatusPulse": {
    "0%, 100%": {
      transform: "scale(1)",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
    },
    "50%": {
      transform: "scale(1.02)",
      boxShadow: "0 4px 12px rgba(56, 176, 0, 0.6)",
    },
  },
  "@keyframes energyTravel": {
    "0%": {
      strokeDasharray: "0 1000",
      strokeDashoffset: 1000,
      opacity: 0,
    },
    "10%": {
      strokeDasharray: "40 1000",
      strokeDashoffset: 960,
      opacity: 0.6,
    },
    "25%": {
      strokeDasharray: "90 1000",
      strokeDashoffset: 850,
      opacity: 0.9,
    },
    "60%": {
      strokeDasharray: "120 1000",
      strokeDashoffset: 500,
      opacity: 1,
    },
    "85%": {
      strokeDasharray: "60 1000",
      strokeDashoffset: 200,
      opacity: 0.8,
    },
    "100%": {
      strokeDasharray: "0 1000",
      strokeDashoffset: 0,
      opacity: 0,
    },
  },
  "@keyframes glowPulse": {
    "0%, 100%": {
      filter: `
        drop-shadow(0 0 8px rgba(232, 163, 61, 0.9))
        drop-shadow(0 0 14px rgba(232, 163, 61, 0.6))
        drop-shadow(0 0 22px rgba(232, 163, 61, 0.4))
      `,
    },
    "50%": {
      filter: `
        drop-shadow(0 0 12px rgba(232, 163, 61, 1))
        drop-shadow(0 0 20px rgba(232, 163, 61, 0.9))
        drop-shadow(0 0 28px rgba(232, 163, 61, 0.7))
      `,
    },
  },
  sectionDivider: {
    backgroundColor: "#b5b1b3 !important",
    margin: "1rem 0 !important",
    height: "1px !important",
    width: "100% !important",
    border: "none !important",
  },
  containerServerInformation: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  containerServerIP: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 1.5rem",
    width: "100%",
  },
  serverIPText: {
    color: "#ffffff",
    fontWeight: 525,
    fontSize: "1rem",
    textTransform: "uppercase",
  },
  containerServerIPValue: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#41474E",
    padding: "0.35rem 1rem",
    borderRadius: 16,
    maxWidth: "70%",
    minWidth: 0,
  },
  serverIP: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.8rem",
  },
  copyIconContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#6c757d",
    padding: "0.4rem",
    cursor: "pointer",
    borderRadius: "50%",
    zIndex: 1,
    transition: "background-color 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: "#7b848d",
    },
  },
  copyIcon: {
    color: "#ffffff",
    fontSize: "1rem",
  },
  copyTooltip: {
    backgroundColor: "#1C1E20",
    padding: "0.3rem 0.5rem",
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  copyTooltipArrow: {
    color: "#1C1E20",
  },
  containerServerLocation: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 1.5rem",
    marginTop: "0.8rem",
    width: "100%",
  },
  serverLocationText: {
    color: "#ffffff",
    fontWeight: 525,
    fontSize: "1rem",
    textTransform: "uppercase",
  },
  containerLocation: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.8rem",
    backgroundColor: "#41474E",
    padding: "0.35rem 1rem",
    borderRadius: 14,
  },
  containerMatchInformations: {
    position: "relative",
    zIndex: 1,
    background:
      "linear-gradient(180deg, rgba(18,18,18,0.96) 0%, rgba(18,18,18,0.84) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    top: 12,
    borderRadius: 24,
    minHeight: "25rem",
    width: "100%",
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.34)",
    backdropFilter: "blur(14px)",
  },
  matchPlayerCardViewSwitcher: {
    display: "flex",
    justifyContent: "center",
    padding: "0 1.5rem 1rem",
  },
  playerCardViewToggleGroup: {
    backgroundColor: "#1C1E20",
    borderRadius: "999px",
    padding: "0.2rem",
    gap: "0.25rem",
    "& .MuiToggleButtonGroup-grouped": {
      margin: 0,
      border: "none",
    },
  },
  playerCardViewToggle: {
    borderRadius: "999px !important",
    border: "none !important",
    color: "#b5b1b3 !important",
    padding: "0.45rem 0.95rem",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
    "&.Mui-selected": {
      backgroundColor: "#E8A33D !important",
      color: "#ffffff !important",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#E8A33D !important",
    },
  },
  containerMatchScoreAndTimer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  containerMatchScore: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    marginTop: "1rem",
  },
  teamScoreText: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    fontFamily: "Roboto",
    color: "#ffffff",
    fontWeight: "bold",
    fontSize: "2rem",
  },
  layoutTeamWinner: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  trophyIcon: {
    color: "#FDD808",
    height: "2.2rem",
    width: "2.2rem",
  },
  containerMatchDuration: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "0.8rem",
    gap: "0.5rem",
  },
  clockLayout: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  mmOptionRowCb: {
    height: 30,
  },
  minHoursPlayedField: {
    width: 100,
    fontSize: 11,
  },
  anim: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.6rem",
    width: "100%",
    padding: "0.1rem 0",
    boxSizing: "border-box",
    minWidth: 0,
    overflow: "hidden",
    "& .MuiTypography-root": {
      color: "#b5b1b3",
      fontSize: "0.9rem",
      fontWeight: 500,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  cancelReasonTooltip: {
    backgroundColor: "#1C1E20",
    padding: "0.5rem 0.65rem",
    fontSize: "0.8rem",
    fontWeight: 500,
    maxWidth: 420,
    whiteSpace: "normal",
    lineHeight: 1.35,
  },
  cancelReasonTooltipArrow: {
    color: "#1C1E20",
  },
  gameStatusCont: {
    position: "absolute",
    paddingLeft: 30,
    width: 200,
    left: 116,
    top: 142,
  },
  serverCont: {
    position: "absolute",
    paddingLeft: 30,
    width: 275,
    left: 144,
    top: 86,
  },
  serverIcon: {
    position: "absolute",
    top: 11,
    left: 0,
  },
  serverAvatar: {
    width: "1.6rem",
    height: "1.6rem",
  },
  containerButtonScoreboard: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    margin: "1.8rem 0 1rem 0",
  },
  leaverSummaryCard: {
    margin: "0 1.5rem 1.5rem",
    padding: "1rem 1.1rem",
    borderRadius: 12,
    border: "1px solid rgba(232, 163, 61, 0.35)",
    backgroundColor: "rgba(232, 163, 61, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "0.65rem",
  },
  leaverSummaryEyebrow: {
    color: "#A78BFA",
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  leaverSummaryTitle: {
    color: "#ffffff",
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: 1.3,
  },
  leaverSummaryText: {
    color: "#d7d7d7",
    fontSize: "0.9rem",
    lineHeight: 1.5,
  },
  leaverSummaryMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
  },
  leaverSummaryMetaChip: {
    color: "#ffffff",
    fontSize: "0.75rem",
    fontWeight: 600,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: 999,
    padding: "0.35rem 0.65rem",
  },
  leaverSummaryButton: {
    alignSelf: "flex-start",
    backgroundColor: "#E8A33D",
    color: "#ffffff",
    fontSize: "0.9rem",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 999,
    padding: "0.55rem 1rem",
    "&:hover": {
      backgroundColor: "#E55A00",
    },
  },
  btnScoreboard: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.4rem",
    padding: "0.52rem 1.15rem",
    borderRadius: "999px",
    border: "none",
    color: "#ffffff !important",
    backgroundColor: "#E8A33D !important",
    boxShadow: "0 8px 18px rgba(232, 163, 61, 0.22)",
    transition:
      "background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: "#E55A00 !important",
      boxShadow: "0 10px 22px rgba(229, 90, 0, 0.28)",
    },
  },
  btnScoreboardText: {
    fontWeight: 700,
    fontSize: "0.88rem",
    lineHeight: 1,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#ffffff",
  },
  scoreboardIcon: {
    marginLeft: 0,
    fontSize: "1.05rem",
    color: "#ffffff",
  },
  controlCont: {
    position: "absolute",
    left: 254,
    bottom: 15,
  },
  gameStatusText: {
    fontWeight: 525,
    color: "#666666",
    fontSize: 15,
  },
  vspacer10: {
    height: 10,
  },
  matchNotFoundContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 200px)",
    padding: "2rem",
  },
  matchNotFoundCard: {
    background:
      "linear-gradient(180deg, rgba(18,18,18,0.96) 0%, rgba(18,18,18,0.84) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: "3rem 2.5rem",
    maxWidth: 500,
    width: "100%",
    textAlign: "center",
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.34)",
    backdropFilter: "blur(16px)",
  },
  matchNotFoundIcon: {
    fontSize: "5rem",
    color: "#E8A33D",
    marginBottom: "1.5rem",
    filter: "drop-shadow(0 0 8px rgba(232, 163, 61, 0.4))",
  },
  matchNotFoundTitle: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "1rem",
    letterSpacing: "0.5px",
  },
  matchNotFoundText: {
    fontSize: "1rem",
    color: "#b5b1b3",
    marginBottom: "2rem",
    lineHeight: 1.6,
  },
  matchNotFoundButton: {
    backgroundColor: "#E8A33D",
    background: "linear-gradient(135deg, #E8A33D 0%, #E55A00 100%)",
    color: "#ffffff",
    fontSize: "1rem",
    fontWeight: 600,
    padding: "0.75rem 2rem",
    borderRadius: 8,
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#E55A00",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(232, 163, 61, 0.4)",
    },
  },
  backToLobbyRow: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    marginTop: "1.5rem",
    paddingBottom: "1rem",
  },
  contentSection: {
    width: "100%",
    boxSizing: "border-box",
  },
  scrollHint: {
    color: "rgba(255,255,255,0.72)",
    fontSize: "0.9rem",
    textAlign: "center",
    marginTop: "0.75rem",
    marginBottom: "0.25rem",
  },
  btnBackToLobby: {
    position: "relative",
    backgroundColor: "#E8A33D",
    background: "linear-gradient(135deg, #E8A33D 0%, #E55A00 100%)",
    color: "#ffffff",
    fontSize: "1rem",
    fontWeight: 700,
    padding: "0.95rem 1.8rem",
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    textTransform: "uppercase",
    letterSpacing: "1px",
    boxShadow:
      "0 6px 20px rgba(232, 163, 61, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    overflow: "hidden",
    whiteSpace: "nowrap",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
      transition: "left 0.5s ease-in-out",
    },
    "&:hover": {
      background: "linear-gradient(135deg, #E8A33D 0%, #C4841D 100%)",
      boxShadow:
        "0 8px 25px rgba(232, 163, 61, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
      "&::before": {
        left: "100%",
      },
    },
    "&:active": {
      boxShadow: "0 4px 15px rgba(232, 163, 61, 0.4)",
    },
  },
  "@media (max-width: 1279.95px)": {
    homepageRoot: {
      width: "100%",
      boxSizing: "border-box",
      overflowX: "hidden",
      padding: "0.75rem 0.75rem 2rem",
    },
    lobbyFrame: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: "1rem",
      width: "100%",
      maxWidth: 720,
      margin: "0 auto",
      overflow: "visible",
    },
    teamLayout: {
      width: "100%",
      alignItems: "stretch",
    },
    containerTeamNameLayout: {
      marginBottom: "0.5rem",
      padding: "0 0.15rem",
      boxSizing: "border-box",
    },
    teamLeftName: {
      width: "100%",
      fontSize: "1rem",
    },
    teamRightName: {
      textAlign: "left",
      fontSize: "1rem",
    },
    teamContainer: {
      width: "100%",
      padding: "0.75rem",
      rowGap: "0.75rem",
      borderRadius: 16,
      boxShadow: "0 14px 36px rgba(0, 0, 0, 0.28)",
    },
    teamAnimatedLine: {
      display: "none",
    },
    teamAnimatedLineEnergy: {
      display: "none",
    },
    containerMatchInformations: {
      top: 0,
      minHeight: "auto",
      borderRadius: 18,
      overflow: "hidden",
      order: -1,
    },
    matchPlayerCardViewSwitcher: {
      padding: "0 0.9rem 0.9rem",
      overflowX: "auto",
    },
    playerCardViewToggleGroup: {
      flexShrink: 0,
    },
    playerCardViewToggle: {
      padding: "0.4rem 0.72rem",
      fontSize: "0.66rem",
    },
    containerMatchScore: {
      marginTop: "0.75rem",
    },
    teamScoreText: {
      fontSize: "1.65rem",
    },
    trophyIcon: {
      width: "1.6rem",
      height: "1.6rem",
    },
    containerMatchDuration: {
      flexDirection: "column",
      gap: "0.55rem",
      marginTop: "0.65rem",
    },
    iconDuration: {
      width: "1.35rem",
      height: "1.35rem",
    },
    clockTime: {
      fontSize: "1.15rem",
    },
    containerServerIP: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: "0.45rem",
      padding: "0 0.9rem",
    },
    containerServerIPValue: {
      maxWidth: "100%",
      justifyContent: "center",
    },
    containerServerLocation: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: "0.45rem",
      padding: "0 0.9rem",
    },
    containerLocation: {
      justifyContent: "center",
    },
    joinCountdownHint: {
      justifyContent: "center",
      padding: "0.5rem 0.9rem 0",
    },
    joinCountdownHintText: {
      textAlign: "center",
    },
    containerButtonScoreboard: {
      margin: "1.1rem 0 0.8rem",
    },
    scrollHint: {
      padding: "0 0.9rem",
      fontSize: "0.78rem",
    },
    leaverSummaryCard: {
      margin: "0 0.9rem 1rem",
    },
    leaverSummaryButton: {
      alignSelf: "stretch",
    },
    contentSection: {
      width: "100%",
    },
    backToLobbyRow: {
      marginTop: "0.75rem",
    },
  },
  "@media (min-width: 1280px)": {
    homepageRoot: {
      width: "100%",
      maxWidth: 1500,
      boxSizing: "border-box",
      margin: "1rem auto",
      padding: "1.5rem 1rem 2.5rem",
    },
    lobbyFrame: {
      position: "relative",
      display: "grid",
      gridTemplateColumns: "1fr 22rem 1fr",
      alignItems: "center",
      justifyItems: "center",
      backgroundColor: "transparent !important",
      boxShadow: "none !important",
      borderRadius: "0 !important",
      gap: "clamp(1.5rem, 4vw, 6rem)",
      width: "100%",
      maxWidth: "100%",
    },
    contentSection: {
      gridColumn: "1 / -1",
    },
    backToLobbyRow: {
      gridColumn: "1 / -1",
      justifySelf: "stretch",
    },
  },
});

class MatchPage extends React.Component {
  // ============================================================================
  // CONSTRUCTOR & STATE
  // ============================================================================

  constructor() {
    super();
    this.state = {
      menuAnchor: null,
      menuAnchorSteamId: null,
      relationshipsBySteamId: {}, // { [steamId]: 'friends' | 'pending' | 'none' }
      friendsPollingPid: null,
      relationshipsPollingPid: null,
      nowUnix: Math.floor(Date.now() / 1000),
      copied: false,
      openReportDialog: false,
      reportTarget: null,
      reportedSteamIds: {}, // { [steamId]: true } for this match id
      playerCardViewMode: DEFAULT_MATCH_PLAYER_CARD_VIEW,
    };
    this.unblock = null;
    this.joinCountdownPid = null;
    this.lastScrolledHashKey = null;
  }

  // ============================================================================
  // LIFECYCLE METHODS
  // ============================================================================

  componentDidMount() {
    //Take the match id from the route
    const { id } = this.props.match.params;
    const storedPlayerCardViewMode = readMatchPlayerCardView();

    this.props.actions.getMatchDataByIdWithPolling(id);
    if (
      !Array.isArray(this.props.catalog?.serverLocations) ||
      this.props.catalog.serverLocations.length === 0
    ) {
      this.props.actions.fetchServerLocations();
    }
    if (storedPlayerCardViewMode !== this.state.playerCardViewMode) {
      this.setState({ playerCardViewMode: storedPlayerCardViewMode });
    }
    this.scrollToHashTargetIfReady();

    const { user } = this.props.catalog || {};
    if (user) {
      // Start friends polling like in profile page
      this.props.actions.getFriends({
        userId: user.userId,
        steamId: user.steamId || user.steamId64,
      });
      this.startFriendsPolling(user);
      // Initialize relationships for current match players
      this.refreshRelationshipsForMatchPlayers();
      this.startRelationshipsPolling();
    }

    // Only play when a live matchmaking redirect explicitly arms this match id.
    if (consumeMatchPageLandedSoundForMatch(id)) {
      playMatchPageLandedSound(user);
    }
  }

  componentDidUpdate(prevProps) {
    const { matchData } = this.props.catalog;
    const { id } = this.props.match.params;
    const prevId = prevProps.match?.params?.id;

    if (prevId !== id) {
      this.unblockNavigation();
      this.props.actions.clearMatchData();
      this.props.actions.getMatchDataByIdWithPolling(id);
      this.setState({
        menuAnchor: null,
        menuAnchorSteamId: null,
        relationshipsBySteamId: {},
        copied: false,
        openReportDialog: false,
        reportTarget: null,
      });

      // Only play for explicitly armed redirects (never for ordinary visits).
      if (consumeMatchPageLandedSoundForMatch(id)) {
        playMatchPageLandedSound(this.props.catalog?.user);
      }
    }

    // If user becomes available, start friend polling and relationships
    const prevUser = prevProps.catalog?.user;
    const currUser = this.props.catalog?.user;
    if (
      (!prevUser && currUser) ||
      (prevUser &&
        currUser &&
        (prevUser.steamId || prevUser.steamId64) !==
          (currUser.steamId || currUser.steamId64))
    ) {
      this.props.actions.getFriends({
        userId: currUser.userId,
        steamId: currUser.steamId || currUser.steamId64,
      });
      this.stopFriendsPolling();
      this.startFriendsPolling(currUser);
      this.refreshRelationshipsForMatchPlayers();
      this.stopRelationshipsPolling();
      this.startRelationshipsPolling();
    }

    // If usersMetaData changed (e.g., players list updated), refresh relationships map
    const prevUsersMeta = prevProps.catalog?.usersMetaData;
    const currUsersMeta = this.props.catalog?.usersMetaData;
    if (prevUsersMeta !== currUsersMeta && this.props.catalog?.user) {
      this.refreshRelationshipsForMatchPlayers();
    }

    // If the user is in a live match, prevent navigating to Lobby (other pages are allowed).
    if (matchData) {
      const { isLive, isFinished } = this.getMatchStatus(matchData);
      const isActiveMatch =
        matchData.status &&
        matchData.status !== "FINISHED" &&
        matchData.status !== "CANCELLED";

      if (isLive && isActiveMatch) {
        this.blockNavigation();
      } else {
        this.unblockNavigation();
      }

      this.syncJoinCountdownTicker(matchData, isLive, isFinished);
    } else {
      this.stopJoinCountdownTicker();
    }

    this.scrollToHashTargetIfReady();
  }

  scrollToHashTargetIfReady() {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const hash = window.location.hash;
    if (!hash) {
      this.lastScrolledHashKey = null;
      return;
    }

    const { id } = this.props.match.params;
    const scrollKey = `${id}:${hash}`;
    if (this.lastScrolledHashKey === scrollKey) {
      return;
    }

    const targetId = hash.replace(/^#/, "");
    const targetElement = targetId ? document.getElementById(targetId) : null;
    if (!targetElement) {
      return;
    }

    targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    this.lastScrolledHashKey = scrollKey;
  }

  blockNavigation() {
    if (!this.unblock && this.props.history) {
      this.unblock = this.props.history.block((location) => {
        const pathname = location?.pathname || "";
        if (pathname === "/lobby") {
          const matchId = this.props.match?.params?.id;
          if (matchId) {
            // Redirect back to the active match instead of allowing Lobby.
            setTimeout(() => {
              // Guard against unmount during async.
              if (this.props.history?.replace) {
                this.props.history.replace(`/match/${matchId}`);
              }
            }, 0);
          }
          return false;
        }
        return true;
      });
    }
  }

  unblockNavigation() {
    if (this.unblock) {
      this.unblock();
      this.unblock = null;
    }
  }

  componentWillUnmount() {
    if (this.handleBeforeUnload) {
      window.removeEventListener("beforeunload", this.handleBeforeUnload);
    }
    this.unblockNavigation();
    this.stopJoinCountdownTicker();
    this.stopFriendsPolling();
    this.stopRelationshipsPolling();
    this.props.actions.clearMatchData();
  }

  // ============================================================================
  // JOIN COUNTDOWN (3 MINUTES TO JOIN SERVER)
  // ============================================================================

  shouldShowJoinCountdown(matchData, isLive, isFinished) {
    if (!matchData || isLive || isFinished) return false;
    const setupDoneAt = Number(matchData?.matchSetupDoneAt);
    return Number.isFinite(setupDoneAt) && setupDoneAt > 0;
  }

  getJoinCountdown(matchData, isLive, isFinished) {
    if (!this.shouldShowJoinCountdown(matchData, isLive, isFinished)) {
      return { show: false, timeLeftSeconds: 0, deadlineUnix: null };
    }

    const setupDoneAt = Number(matchData.matchSetupDoneAt);
    const joinWindowSeconds = Number.isFinite(
      Number(matchData?.joinTimeoutSeconds),
    )
      ? Number(matchData.joinTimeoutSeconds)
      : FALLBACK_JOIN_WINDOW_SECONDS;
    const deadlineUnix = setupDoneAt + joinWindowSeconds;
    const timeLeftSeconds = Math.max(
      0,
      deadlineUnix - (this.state.nowUnix || 0),
    );
    return { show: true, timeLeftSeconds, deadlineUnix };
  }

  startJoinCountdownTicker() {
    if (this.joinCountdownPid) return;
    this.joinCountdownPid = setInterval(() => {
      this.setState({ nowUnix: Math.floor(Date.now() / 1000) });
    }, 1000);
    this.setState({ nowUnix: Math.floor(Date.now() / 1000) });
  }

  stopJoinCountdownTicker() {
    if (this.joinCountdownPid) {
      clearInterval(this.joinCountdownPid);
      this.joinCountdownPid = null;
    }
  }

  syncJoinCountdownTicker(matchData, isLive, isFinished) {
    const shouldRun = this.shouldShowJoinCountdown(
      matchData,
      isLive,
      isFinished,
    );
    if (shouldRun) {
      this.startJoinCountdownTicker();
    } else {
      this.stopJoinCountdownTicker();
    }
  }

  // ============================================================================
  // MATCH DATA PROCESSING METHODS
  // ============================================================================

  getMatchStatus(matchData) {
    const status = matchData?.status;
    const recordedGameEvents = Array.isArray(matchData?.recordedGameEvents)
      ? matchData.recordedGameEvents
      : [];

    const goingLiveIndex = recordedGameEvents.findIndex(
      (o) => getGcEventType(o) === "going_live",
    );
    const seriesEndIndex = recordedGameEvents.findIndex(
      (o) => getGcEventType(o) === "series_end",
    );

    const timeStampMatchBegin =
      goingLiveIndex !== -1
        ? getGcEventTimestampUnix(recordedGameEvents[goingLiveIndex])
        : // Fallbacks when we're live but haven't received going_live yet (or payload shape differs)
          normalizeUnixSeconds(matchData?.matchSetupDoneAt) ||
          normalizeUnixSeconds(matchData?.createdAt) ||
          null;

    const timeStampMatchEnd =
      seriesEndIndex !== -1
        ? getGcEventTimestampUnix(recordedGameEvents[seriesEndIndex])
        : null;

    const isFinished =
      status === "FINISHED" || status === "CANCELLED" || seriesEndIndex !== -1;

    const isLive =
      !isFinished && (goingLiveIndex !== -1 || status === "STARTED");

    let statusLabel = "Waiting";
    let mapStatusText = "waiting for players";

    if (isLive) {
      statusLabel = "Live";
      mapStatusText = "live match";
    } else if (status === "CANCELLED") {
      statusLabel = "Cancelled";
      mapStatusText = "match cancelled";
    } else if (isFinished) {
      statusLabel = "Finished";
      mapStatusText = "match finished";
    } else if (status) {
      if (status === "WAITING_FOR_PLAYERS") {
        statusLabel = "Waiting";
        mapStatusText = "waiting for players";
      } else if (status === "ALL_PLAYERS_JOINED") {
        statusLabel = "Ready";
        mapStatusText = "all players joined";
      } else if (
        status === "INIT" ||
        status === "TERRAFORM_SETUP" ||
        status === "TERRAFORM_START" ||
        status === "SERVER_AGENT_WAKEUP_PENDING" ||
        status === "SERVER_AGENT_INITIAL_HB" ||
        status === "SERVER_AGENT_MATCH_SETUP_DONE"
      ) {
        statusLabel = "Setting up";
        mapStatusText = "setting up server";
      } else {
        // Fallback for any new/unknown pre-live statuses
        statusLabel = "Waiting";
        mapStatusText = "waiting";
      }
    }

    return {
      isLive,
      isFinished,
      statusLabel,
      mapStatusText,
      timeStampMatchBegin,
      timeStampMatchEnd,
    };
  }

  processMatchData(matchData) {
    if (!matchData) {
      return {
        lastTeam1Score: 0,
        lastTeam2Score: 0,
        timeStampMatchBegin: null,
        timeStampMatchEnd: null,
        isLive: false,
        isFinished: false,
        statusLabel: "Waiting",
        mapStatusText: "waiting for players",
      };
    }

    let lastTeam1Score = 0;
    let lastTeam2Score = 0;

    const {
      isLive,
      isFinished,
      statusLabel,
      mapStatusText,
      timeStampMatchBegin,
      timeStampMatchEnd,
    } = this.getMatchStatus(matchData);

    const recordedGameEvents = Array.isArray(matchData.recordedGameEvents)
      ? matchData.recordedGameEvents
      : [];

    if (recordedGameEvents.length > 0) {
      const roundEndEvents = recordedGameEvents.filter(
        (event) => getGcEventType(event) === "round_end",
      );
      if (roundEndEvents.length > 0) {
        const lastRoundEndPayload =
          roundEndEvents[roundEndEvents.length - 1]?.payload;
        const body =
          lastRoundEndPayload?.payload?.body || lastRoundEndPayload?.body || {};
        lastTeam1Score = body?.team1?.score ?? 0;
        lastTeam2Score = body?.team2?.score ?? 0;
      }
    }

    return {
      lastTeam1Score,
      lastTeam2Score,
      timeStampMatchBegin,
      timeStampMatchEnd,
      isLive,
      isFinished,
      statusLabel,
      mapStatusText,
    };
  }

  getMatchDuration(matchData, isLive) {
    let min = 0;
    let sec = 0;

    if (!isLive && matchData.matchDuration) {
      const durationInSeconds = matchData.matchDuration;
      min = Math.floor(durationInSeconds / 60);
      sec = Math.floor(durationInSeconds % 60);
    }

    return { min, sec };
  }

  // ============================================================================
  // FRIENDS & RELATIONSHIPS MANAGEMENT
  // ============================================================================

  startFriendsPolling(user) {
    if (this.state.friendsPollingPid) return;
    // One-shot fetch only — ongoing updates arrive via Socket.IO push from the lobby
    this.props.actions.getFriends({
      userId: user.userId,
      steamId: user.steamId,
    });
  }

  stopFriendsPolling() {
    // No-op — polling removed in favour of Socket.IO push
    if (this.state.friendsPollingPid) {
      clearInterval(this.state.friendsPollingPid);
      this.setState({ friendsPollingPid: null });
    }
  }

  startRelationshipsPolling() {
    if (this.state.relationshipsPollingPid) return;
    const pid = setInterval(() => {
      this.refreshRelationshipsForMatchPlayers();
    }, 10000);
    this.setState({ relationshipsPollingPid: pid });
  }

  stopRelationshipsPolling() {
    if (this.state.relationshipsPollingPid) {
      clearInterval(this.state.relationshipsPollingPid);
      this.setState({ relationshipsPollingPid: null });
    }
  }

  getAllOtherPlayersSteamIds() {
    const { catalog } = this.props;
    const userSteamId = catalog?.user?.steamId;
    const team1 = catalog?.usersMetaData?.team1 || [];
    const team2 = catalog?.usersMetaData?.team2 || [];
    const allPlayers = [...team1, ...team2];
    return allPlayers
      .map((p) => p.steamId)
      .filter((steamId) => steamId && steamId !== userSteamId);
  }

  async refreshRelationshipsForMatchPlayers() {
    const { catalog } = this.props;
    const viewerSteamId = catalog?.user?.steamId;
    if (!viewerSteamId) return;

    const steamIds = this.getAllOtherPlayersSteamIds();
    if (steamIds.length === 0) return;

    // Fetch relationships in parallel; avoid spamming if already known friends via catalog.friends
    const friendsList = this.props.catalog?.friends || [];
    const friendSteamSet = new Set(friendsList.map((f) => f.friendSteamId));

    const updates = {};
    steamIds.forEach((steamId) => {
      if (friendSteamSet.has(steamId)) {
        updates[steamId] = "friends";
      }
    });

    const toQuery = steamIds.filter((steamId) => !friendSteamSet.has(steamId));
    try {
      const fetchPromises = toQuery.map(async (targetSteamId) => {
        try {
          const status = await this.props.actions.getProfileRelationship({
            viewerSteamId,
            targetSteamId,
          });
          if (status === "friends") return [targetSteamId, "friends"];
          if (status === "pending_outgoing" || status === "pending_incoming")
            return [targetSteamId, "pending"];
          return [targetSteamId, "none"];
        } catch (e) {
          return [targetSteamId, "none"];
        }
      });

      const results = await Promise.all(fetchPromises);
      results.forEach(([steamId, status]) => {
        updates[steamId] = status;
      });
    } catch (e) {
      // ignore network errors; keep existing state
    }

    if (Object.keys(updates).length > 0) {
      this.setState((prev) => ({
        relationshipsBySteamId: { ...prev.relationshipsBySteamId, ...updates },
      }));
    }
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  validateForm = (e) => {
    e.preventDefault();
  };

  handleMenuOpen = (event) => {
    const anchorSteamId =
      event.currentTarget?.getAttribute("data-steamid") || null;
    this.setState({
      menuAnchor: event.currentTarget,
      menuAnchorSteamId: anchorSteamId,
    });
  };

  handleMenuClose = () => {
    this.setState({ menuAnchor: null, menuAnchorSteamId: null });
  };

  handleProfile = (playerId) => {
    this.props.history.push(`/profile/${playerId}`);
    this.handleMenuClose();
  };

  handleAddFriend = (playerSteamId) => {
    const { user } = this.props.catalog;
    if (notifyIfCannotSendFriendRequest(user)) {
      return;
    }
    this.props.actions.sendFriendRequest({
      recipientSteamId: playerSteamId,
      userId: user.userId,
      userSteamId: user.steamId,
    });

    this.setState((prev) => ({
      relationshipsBySteamId: {
        ...prev.relationshipsBySteamId,
        [playerSteamId]: "pending",
      },
    }));
    this.handleMenuClose();
  };

  handleCancelFriendRequestFromMatchPage = (steamId) => {
    const { user } = this.props.catalog;
    this.props.actions.cancelFriendRequest({
      recipientSteamId: steamId,
      userId: user.userId,
      userSteamId: user.steamId,
    });

    // Immediately update local state
    this.setState((prev) => ({
      relationshipsBySteamId: {
        ...prev.relationshipsBySteamId,
        [steamId]: "none",
      },
    }));
    this.handleMenuClose();
  };

  handleCopyServerIP = async (serverIP) => {
    try {
      const ip = (serverIP || "").toString().trim();
      if (!ip) return;

      // If the UI shows only an IP, we want a CS2 console-ready command.
      // Example: `connect 1.2.3.4:27015`
      const address = ip.includes(":") ? ip : `${ip}:27015`;
      await navigator.clipboard.writeText(`connect ${address}`);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    } catch (err) {
      console.error("Failed to copy server IP:", err);
    }
  };

  openReportDialog = (steamId, nickname) => {
    this.setState({
      reportTarget: { steamId, nickname },
      openReportDialog: true,
    });
  };

  closeReportDialog = () => {
    this.setState({
      openReportDialog: false,
      reportTarget: null,
    });
  };

  handleReportSubmitted = (result) => {
    try {
      const reportedSteamId =
        result?.reportedSteamId || this.state.reportTarget?.steamId || null;
      if (result?.ok && reportedSteamId) {
        this.setState((prev) => ({
          reportedSteamIds: {
            ...(prev.reportedSteamIds || {}),
            [String(reportedSteamId)]: true,
          },
        }));
      }
    } catch (e) {
      // ignore
    }
  };

  handlePlayerCardViewModeChange = (_event, nextViewMode) => {
    if (!nextViewMode) {
      return;
    }

    const resolvedViewMode = writeMatchPlayerCardView(nextViewMode);
    this.setState({
      playerCardViewMode: resolvedViewMode || DEFAULT_MATCH_PLAYER_CARD_VIEW,
    });
  };

  // ============================================================================
  // RENDER METHOD
  // ============================================================================

  render() {
    const { classes, actions, catalog } = this.props;
    const {
      user,
      matchData,
      usersMetaData,
      scoreboardData,
      matchAnalysis,
    } = catalog;
    const currentMatchId = this.props.match.params.id;
    const matchPageView = getMatchPageViewState({
      routeMatchId: currentMatchId,
      matchData,
      matchPageMissingId: catalog.matchPageMissingId,
    });

    const { menuAnchor } = this.state;

    if (matchPageView === "loading") {
      return (
        <div className={classes.loadingContainer}>
          <Paper className={classes.loadingCard}>
            <CircularProgress
              size={60}
              thickness={4}
              className={classes.loadingSpinner}
            />
            <Typography className={classes.loadingTitle}>
              Loading Match
            </Typography>
            <Typography className={classes.loadingText}>
              Please wait while we fetch the match details...
            </Typography>
          </Paper>
        </div>
      );
    }

    if (matchPageView === "missing") {
      return (
        <div className={classes.matchNotFoundContainer}>
          <Paper className={classes.matchNotFoundCard}>
            <SearchIcon className={classes.matchNotFoundIcon} />
            <Typography className={classes.matchNotFoundTitle}>
              Match Not Found
            </Typography>
            <Typography className={classes.matchNotFoundText}>
              We couldn't find the match you're looking for. It may have been
              removed or the link might be incorrect.
            </Typography>
            <Button
              component={Link}
              to="/lobby"
              className={classes.matchNotFoundButton}
            >
              Return to Lobby
            </Button>
          </Paper>
        </div>
      );
    }

    const normalizeSteamId = (v) => (v != null ? String(v).trim() : "");
    const viewerSteamId = normalizeSteamId(
      user?.steamId || user?.steamId64 || user?.steam64Id || "",
    );
    const viewerUserId = user?.userId ? String(user.userId) : "";

    const matchSteamIds = new Set(
      []
        .concat(matchData?.team1 || [], matchData?.team2 || [])
        .map((p) =>
          normalizeSteamId(p?.steamId || p?.steamId64 || p?.steam64Id || ""),
        )
        .filter(Boolean),
    );
    const matchUserIds = new Set(
      []
        .concat(matchData?.team1 || [], matchData?.team2 || [])
        .map((p) => (p?.userId ? String(p.userId) : ""))
        .filter(Boolean),
    );

    const viewerParticipated =
      (!!viewerUserId && matchUserIds.has(viewerUserId)) ||
      (!!viewerSteamId && matchSteamIds.has(viewerSteamId));

    const isCurrentUserAPlayerOfTheMatch = !!user && viewerParticipated;

    const mapNameFromMatch = matchData.map;
    const mapName =
      getMapMetaById(mapNameFromMatch)?.display ||
      mapNameFromMatch ||
      "Unknown Map";

    const {
      lastTeam1Score,
      lastTeam2Score,
      timeStampMatchBegin,
      timeStampMatchEnd,
      isLive,
      isFinished,
      statusLabel,
      mapStatusText,
    } = this.processMatchData(matchData);

    const serverIp = matchData?.outputs?.public_ip || "";
    const canShowServerIp = canShowMatchServerIp({
      serverIp,
      status: matchData?.status,
      isFinished,
      viewerParticipated,
    });
    const isCancelled = matchData?.status === "CANCELLED";
    const isServerPreparingForViewer =
      !isFinished && viewerParticipated && !canShowServerIp;
    const showServerIpRow =
      canShowServerIp || isServerPreparingForViewer || isCancelled;
    const cancelReasonRaw = (matchData?.reason || "").toString().trim();
    const cancelReason = cancelReasonRaw ? cancelReasonRaw : null;

    const { min, sec } = this.getMatchDuration(matchData, isLive);
    const joinCountdown = this.getJoinCountdown(matchData, isLive, isFinished);
    const joinCountdownText = formatCountdownMmSs(
      joinCountdown.timeLeftSeconds,
    );
    const canDownloadDemo = canShowMatchDemoDownload({
      isLoggedIn: !!user,
      demoAvailable: matchData?.demoAvailable,
    });
    const demoDownloadUrl = canDownloadDemo
      ? buildMatchDemoDownloadUrl({
          matchId: currentMatchId,
          edgeBasePath: utils.getEdgeBasePath(),
        })
      : null;
    const serverLocation = getMatchServerLocation({
      matchData,
      serverLocations: catalog?.serverLocations,
    });
    const team1Players = Array.isArray(usersMetaData?.team1)
      ? usersMetaData.team1
      : [];
    const team2Players = Array.isArray(usersMetaData?.team2)
      ? usersMetaData.team2
      : [];

    const STACK_PALETTE = ['#E8A33D', '#22D3EE', '#F472B6', '#FBBF24', '#34D399', '#F87171'];
    const stackColorByPartyId = (() => {
      const allPlayers = [
        ...(usersMetaData?.team1 ?? []),
        ...(usersMetaData?.team2 ?? []),
      ];
      const partyIds = Array.from(new Set(
        allPlayers
          .filter(p => p && p.partyId && p.partySize >= 2)
          .map(p => p.partyId)
      )).sort();
      const map = {};
      partyIds.forEach((id, i) => { map[id] = STACK_PALETTE[i % STACK_PALETTE.length]; });
      return map;
    })();
    const team1LeadPlayer = team1Players[0] || null;
    const team2LeadPlayer = team2Players[0] || null;
    const team1DisplayName = getTeamDisplayName(team1LeadPlayer, "Team 1");
    const team2DisplayName = getTeamDisplayName(team2LeadPlayer, "Team 2");
    const winnerTeam = getResolvedWinnerTeam(matchData);
    const surrenderAnnotation = getSurrenderAnnotation(matchData);
    const viewerLeaverAppeal = getViewerLeaverPenaltyAppealContext({
      matchId: currentMatchId,
      match: matchData,
      viewer: user,
    });

    // Public viewers can still view match + player profiles; report is gated separately.

    return (
      <div className={classes.homepageRoot}>
        <Paper className={classes.lobbyFrame}>
          <>
            <div className={classes.teamLayout}>
              {team1LeadPlayer && (
                <div className={classes.containerTeamNameLayout}>
                  <Typography className={classes.teamLeftName}>
                    {team1DisplayName}
                  </Typography>
                  {!isLive && winnerTeam === "team1" ? (
                    <EmojiEventsIcon
                      className={classes.trophyIconWinnerLabel}
                    />
                  ) : null}
                </div>
              )}
              <div className={classes.teamContainer}>
                <svg
                  className={`${classes.teamAnimatedLine} ${classes.upLeftAnimatedLine}`}
                  width="262"
                  height="220"
                  viewBox="0 0 262 270"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.62939e-06 269.5 H130.5 V0.5L222 0.500006"
                    stroke="#b5b1b3"
                  />
                </svg>
                {isLive && (
                  <svg
                    className={`${classes.teamAnimatedLineEnergy} ${classes.teamAnimatedLineEnergyActive} ${classes.upLeftAnimatedLine}`}
                    width="262"
                    height="220"
                    viewBox="0 0 262 270"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.62939e-06 269.5 H130.5 V0.5L222 0.500006"
                      stroke="#E8A33D"
                    />
                  </svg>
                )}

                <svg
                  className={`${classes.teamAnimatedLine} ${classes.downLeftAnimatedLine}`}
                  width="262"
                  height="220"
                  viewBox="0 0 262 270"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.62939e-06 269.5 H130.5 V0.5L222 0.500006"
                    stroke="#b5b1b3"
                  />
                </svg>
                {isLive && (
                  <svg
                    className={`${classes.teamAnimatedLineEnergy} ${classes.teamAnimatedLineEnergyActive} ${classes.downLeftAnimatedLine}`}
                    width="262"
                    height="220"
                    viewBox="0 0 262 270"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.62939e-06 269.5 H130.5 V0.5L222 0.500006"
                      stroke="#E8A33D"
                    />
                  </svg>
                )}

                {team1Players.length > 0
                  ? team1Players.map((player, index) => {
                      const sid =
                        player?.steamId ||
                        player?.steamId64 ||
                        player?.steam64Id ||
                        null;
                      const sidKey = sid != null ? String(sid) : null;
                      return (
                        <MatchPlayerBox
                          key={player.steamId || index}
                          player={player}
                          currentUser={user}
                          avatarCacheBust={catalog?.avatarCacheBust || 0}
                          relationship={
                            sidKey
                              ? this.state.relationshipsBySteamId[sidKey]
                              : undefined
                          }
                          menuAnchor={menuAnchor}
                          menuAnchorSteamId={this.state.menuAnchorSteamId}
                          onMenuOpen={this.handleMenuOpen}
                          onMenuClose={this.handleMenuClose}
                          onProfileClick={this.handleProfile}
                          onAddFriend={this.handleAddFriend}
                          onCancelFriendRequest={
                            this.handleCancelFriendRequestFromMatchPage
                          }
                          onReportClick={this.openReportDialog}
                          isCurrentUserAPlayerOfTheMatch={
                            isCurrentUserAPlayerOfTheMatch
                          }
                          viewMode={this.state.playerCardViewMode}
                          canReport={
                            !!user &&
                            viewerParticipated &&
                            (() => {
                              const targetSteamId = normalizeSteamId(
                                player?.steamId ||
                                  player?.steamId64 ||
                                  player?.steam64Id ||
                                  "",
                              );
                              const targetUserId = player?.userId
                                ? String(player.userId)
                                : "";
                              if (!targetSteamId) return false;
                              if (
                                viewerSteamId &&
                                targetSteamId === viewerSteamId
                              )
                                return false;
                              if (
                                viewerUserId &&
                                targetUserId &&
                                targetUserId === viewerUserId
                              )
                                return false;
                              if (
                                (this.state.reportedSteamIds || {})[
                                  targetSteamId
                                ]
                              )
                                return false;
                              return true;
                            })()
                          }
                          alreadyReported={(() => {
                            const targetSteamId = normalizeSteamId(
                              player?.steamId ||
                                player?.steamId64 ||
                                player?.steam64Id ||
                                "",
                            );
                            return (
                              !!targetSteamId &&
                              !!(this.state.reportedSteamIds || {})[
                                targetSteamId
                              ]
                            );
                          })()}
                          partyColor={player.partyId && player.partySize >= 2 ? stackColorByPartyId[player.partyId] : undefined}
                        />
                      );
                    })
                  : null}
              </div>
            </div>

            <div className={classes.containerMatchInformations}>
              {matchData && (
                <MapCard
                  name={mapName}
                  mapId={mapNameFromMatch}
                  isLive={isLive}
                  statusText={mapStatusText}
                />
              )}

              <div className={classes.matchPlayerCardViewSwitcher}>
                <ToggleButtonGroup
                  exclusive
                  value={this.state.playerCardViewMode}
                  onChange={this.handlePlayerCardViewModeChange}
                  className={classes.playerCardViewToggleGroup}
                  size="small"
                >
                  {MATCH_PLAYER_CARD_VIEW_OPTIONS.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      className={classes.playerCardViewToggle}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>

              <div className={classes.containerMatchScoreAndTimer}>
                <div className={classes.containerMatchScore}>
                  <Typography className={classes.teamScoreText}>
                    <span className={classes.layoutTeamWinner}>
                      {winnerTeam === "team1" ? (
                        <EmojiEventsIcon className={classes.trophyIcon} />
                      ) : null}
                      {lastTeam1Score}
                    </span>{" "}
                    -
                    <span className={classes.layoutTeamWinner}>
                      {lastTeam2Score}
                      {winnerTeam === "team2" ? (
                        <EmojiEventsIcon className={classes.trophyIcon} />
                      ) : null}
                    </span>
                  </Typography>
                </div>

                <div className={classes.containerMatchDuration}>
                  <div className={classes.clockLayout}>
                    <AccessTimeIcon className={classes.iconDuration} />
                    {!isLive ? (
                      <div className={classes.clockTextStack}>
                        {joinCountdown.show && (
                          <Typography className={classes.joinCountdownLabel}>
                            Time to join
                          </Typography>
                        )}
                        <Typography className={classes.clockTime}>
                          {joinCountdown.show
                            ? joinCountdownText
                            : `${min}:${sec.toString().padStart(2, "0")}`}
                        </Typography>
                      </div>
                    ) : (
                      <GameDurationClock
                        timestamp={timeStampMatchBegin}
                        isLive={isLive}
                        className={classes.clockTime}
                      />
                    )}
                  </div>
                  <div className={classes.labelMatchStatus}>
                    <Typography
                      className={`${classes.labelMatchStatusText} ${
                        isLive
                          ? classes.labelMatchStatusTextLive
                          : isFinished
                            ? classes.labelMatchStatusTextFinished
                            : classes.labelMatchStatusTextPending
                      }`}
                    >
                      {statusLabel}
                    </Typography>
                  </div>
                  {surrenderAnnotation ? (
                    <Typography className={classes.matchEndAnnotation}>
                      {surrenderAnnotation}
                    </Typography>
                  ) : null}
                </div>

                <div className={classes.sectionDivider} />

                <div className={classes.containerServerInformation}>
                  {showServerIpRow && (
                    <div className={classes.containerServerIP}>
                      <div>
                        <Typography className={classes.serverIPText}>
                          IP:
                        </Typography>
                      </div>
                      <div className={classes.containerServerIPValue}>
                        {matchData?.recordedGameEvents &&
                        matchData.recordedGameEvents.length > 0 &&
                        matchData.recordedGameEvents[
                          matchData.recordedGameEvents.length - 1
                        ] &&
                        getGcEventType(
                          matchData.recordedGameEvents[
                            matchData.recordedGameEvents.length - 1
                          ],
                        ) === "terraform_start" ? (
                          <div className={classes.anim}>
                            <CircularProgress
                              color="secondary"
                              size={18}
                              thickness={5}
                            />
                            <Typography>Creating the server...</Typography>
                          </div>
                        ) : isCancelled ? (
                          <div className={classes.anim}>
                            <Tooltip
                              title={
                                cancelReason ||
                                "This match was cancelled due to a server error."
                              }
                              placement="top"
                              arrow
                              classes={{
                                tooltip: classes.cancelReasonTooltip,
                                arrow: classes.cancelReasonTooltipArrow,
                              }}
                            >
                              <Typography>
                                Match cancelled
                                {cancelReason ? " (hover for details)" : ""}
                              </Typography>
                            </Tooltip>
                          </div>
                        ) : !canShowServerIp ? (
                          <div className={classes.anim}>
                            <CircularProgress
                              color="secondary"
                              size={18}
                              thickness={5}
                            />
                            <Typography>Preparing match...</Typography>
                          </div>
                        ) : (
                          <div className={classes.serverIP}>
                            <Typography className={classes.serverIPText}>
                              {serverIp}
                            </Typography>
                            <Tooltip
                              title={this.state.copied ? "Copied!" : "Copy"}
                              placement="bottom"
                              arrow
                              classes={{
                                tooltip: classes.copyTooltip,
                                arrow: classes.copyTooltipArrow,
                              }}
                            >
                              <div
                                className={classes.copyIconContainer}
                                onClick={() =>
                                  this.handleCopyServerIP(serverIp)
                                }
                              >
                                {this.state.copied ? (
                                  <CheckIcon className={classes.copyIcon} />
                                ) : (
                                  <FileCopyIcon className={classes.copyIcon} />
                                )}
                              </div>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {joinCountdown.show && canShowServerIp && (
                    <div className={classes.joinCountdownHint}>
                      <Typography className={classes.joinCountdownHintText}>
                        Join now — match cancels in{" "}
                        <span className={classes.joinCountdownHintTime}>
                          {joinCountdownText}
                        </span>
                      </Typography>
                    </div>
                  )}

                  <div
                    className={classes.containerServerLocation}
                    style={{ marginBottom: isLive ? "1rem" : "0" }}
                  >
                    <Typography className={classes.serverLocationText}>
                      Server:
                    </Typography>
                    <div className={classes.containerLocation}>
                      <Typography
                        className={classes.serverLocationText}
                        style={{ textTransform: "none" }}
                      >
                        {serverLocation?.label || "Pending"}
                      </Typography>
                      {serverLocation?.flagImageUrl ? (
                        <img
                          className={classes.serverAvatar}
                          src={serverLocation.flagImageUrl}
                          alt=""
                          aria-hidden="true"
                        />
                      ) : null}
                    </div>
                  </div>

                  <div className={classes.containerButtonScoreboard}>
                    {canDownloadDemo && (
                      <Button
                        component="a"
                        href={demoDownloadUrl}
                        variant="contained"
                        className={classes.btnScoreboard}
                      >
                        <Typography className={classes.btnScoreboardText}>
                          Demo
                        </Typography>
                        <DownloadIcon className={classes.scoreboardIcon} />
                      </Button>
                    )}
                  </div>
                  <Typography
                    className={classes.scrollHint}
                    style={{ fontSize: "0.75rem" }}
                  >
                    Scroll down for the full scoreboard, anti-cheat analysis,
                    and round-economy breakdown.
                  </Typography>
                  {viewerLeaverAppeal ? (
                    <div className={classes.leaverSummaryCard}>
                      <Typography className={classes.leaverSummaryEyebrow}>
                        Leaver penalty
                      </Typography>
                      <Typography className={classes.leaverSummaryTitle}>
                        This match recorded you as the leaver.
                      </Typography>
                      <Typography className={classes.leaverSummaryText}>
                        Your reliability reputation was affected for this match.
                        If this was incorrect or there were exceptional
                        circumstances, open a support appeal with the match
                        context prefilled.
                      </Typography>
                      <div className={classes.leaverSummaryMetaRow}>
                        <Typography className={classes.leaverSummaryMetaChip}>
                          Match ID: {viewerLeaverAppeal.matchId}
                        </Typography>
                        <Typography className={classes.leaverSummaryMetaChip}>
                          Status:{" "}
                          {viewerLeaverAppeal.leaver?.status ===
                          "reputation_applied"
                            ? "Penalty applied"
                            : "Recorded"}
                        </Typography>
                      </div>
                      <Button
                        component={Link}
                        to={viewerLeaverAppeal.appealUrl}
                        variant="contained"
                        className={classes.leaverSummaryButton}
                      >
                        Appeal this outcome
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className={classes.teamLayout}>
              {team2LeadPlayer && (
                <div className={classes.containerTeamNameLayout}>
                  <Typography className={classes.teamRightName}>
                    {team2DisplayName}
                  </Typography>
                  {!isLive && winnerTeam === "team2" ? (
                    <EmojiEventsIcon
                      className={classes.trophyIconWinnerLabel}
                    />
                  ) : null}
                </div>
              )}
              <div className={classes.teamContainer}>
                <svg
                  className={`${classes.teamAnimatedLine} ${classes.upRightAnimatedLine}`}
                  width="261"
                  height="220"
                  viewBox="0 0 261 270"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.62939e-06 269.5 H130.5 V0.5L221 0.500006"
                    stroke="#b5b1b3"
                  />
                </svg>
                {isLive && (
                  <svg
                    className={`${classes.teamAnimatedLineEnergy} ${classes.teamAnimatedLineEnergyActive} ${classes.upRightAnimatedLine}`}
                    width="261"
                    height="220"
                    viewBox="0 0 261 270"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.62939e-06 269.5 H130.5 V0.5L221 0.500006"
                      stroke="#E8A33D"
                    />
                  </svg>
                )}

                <svg
                  className={`${classes.teamAnimatedLine} ${classes.downRightAnimatedLine}`}
                  width="261"
                  height="220"
                  viewBox="0 0 261 270"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.62939e-06 269.5 H130.5 V0.5L221 0.500006"
                    stroke="#b5b1b3"
                  />
                </svg>
                {isLive && (
                  <svg
                    className={`${classes.teamAnimatedLineEnergy} ${classes.teamAnimatedLineEnergyActive} ${classes.downRightAnimatedLine}`}
                    width="261"
                    height="220"
                    viewBox="0 0 261 270"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.62939e-06 269.5 H130.5 V0.5L221 0.500006"
                      stroke="#E8A33D"
                    />
                  </svg>
                )}
                {team2Players.length > 0
                  ? team2Players.map((player, index) => {
                      const sid =
                        player?.steamId ||
                        player?.steamId64 ||
                        player?.steam64Id ||
                        null;
                      const sidKey = sid != null ? String(sid) : null;
                      return (
                        <MatchPlayerBox
                          key={player.steamId || index}
                          player={player}
                          currentUser={user}
                          avatarCacheBust={catalog?.avatarCacheBust || 0}
                          relationship={
                            sidKey
                              ? this.state.relationshipsBySteamId[sidKey]
                              : undefined
                          }
                          menuAnchor={menuAnchor}
                          menuAnchorSteamId={this.state.menuAnchorSteamId}
                          onMenuOpen={this.handleMenuOpen}
                          onMenuClose={this.handleMenuClose}
                          onProfileClick={this.handleProfile}
                          onAddFriend={this.handleAddFriend}
                          onCancelFriendRequest={
                            this.handleCancelFriendRequestFromMatchPage
                          }
                          onReportClick={this.openReportDialog}
                          isCurrentUserAPlayerOfTheMatch={
                            isCurrentUserAPlayerOfTheMatch
                          }
                          viewMode={this.state.playerCardViewMode}
                          canReport={
                            !!user &&
                            viewerParticipated &&
                            (() => {
                              const targetSteamId = normalizeSteamId(
                                player?.steamId ||
                                  player?.steamId64 ||
                                  player?.steam64Id ||
                                  "",
                              );
                              const targetUserId = player?.userId
                                ? String(player.userId)
                                : "";
                              if (!targetSteamId) return false;
                              if (
                                viewerSteamId &&
                                targetSteamId === viewerSteamId
                              )
                                return false;
                              if (
                                viewerUserId &&
                                targetUserId &&
                                targetUserId === viewerUserId
                              )
                                return false;
                              if (
                                (this.state.reportedSteamIds || {})[
                                  targetSteamId
                                ]
                              )
                                return false;
                              return true;
                            })()
                          }
                          alreadyReported={(() => {
                            const targetSteamId = normalizeSteamId(
                              player?.steamId ||
                                player?.steamId64 ||
                                player?.steam64Id ||
                                "",
                            );
                            return (
                              !!targetSteamId &&
                              !!(this.state.reportedSteamIds || {})[
                                targetSteamId
                              ]
                            );
                          })()}
                          partyColor={player.partyId && player.partySize >= 2 ? stackColorByPartyId[player.partyId] : undefined}
                        />
                      );
                    })
                  : null}
              </div>
            </div>

            <div className={classes.contentSection}>
              <MatchScoreboardSection
                scoreboardData={scoreboardData}
                matchData={matchData}
                lastTeam1Score={lastTeam1Score}
                lastTeam2Score={lastTeam2Score}
                isLive={isLive}
              />
            </div>

            <div className={classes.contentSection}>
              <MatchAnalysisSection
                sectionType="anticheat"
                eyebrow="Anti-Cheat Analysis"
                title="Per-player telemetry review"
                summary={matchAnalysis?.anticheat?.summary || null}
                players={matchAnalysis?.anticheat?.players || []}
                emptyTitle="Anti-cheat telemetry is still pending."
                emptyText="This section appears as soon as the match has anti-cheat observations attached to it."
              />
            </div>

            <div className={classes.contentSection}>
              <MatchAnalysisSection
                sectionType="stats"
                eyebrow="Stats Based Analysis"
                title="Per-player performance and engagement metrics"
                summary={{
                  headline: "Performance metrics are informational while detection is in a learning phase.",
                  detail:
                    "These match-local stats help us build baselines for future analysis, but they do not flag players on their own today.",
                }}
                players={matchAnalysis?.anticheat?.players || []}
                emptyTitle="Stats analysis is still pending."
                emptyText="This section appears once player telemetry and impact events are available for the match."
              />
            </div>

            <div className={classes.contentSection}>
              <MatchAnalysisSection
                sectionType="economy"
                eyebrow="Money Performance"
                title="Round-economy and utility discipline"
                summary={matchAnalysis?.economy?.summary || null}
                players={matchAnalysis?.economy?.players || []}
                emptyTitle="Round-economy summary is still pending."
                emptyText="This section appears once the match economy summary has been generated from round-by-round purchase data."
              />
            </div>

            <div className={classes.contentSection}>
              <MatchAnalysisSection
                sectionType="utility"
                eyebrow="Utility Impact"
                title="Purchases used, saved, wasted and utility damage"
                summary={matchAnalysis?.utility?.summary || null}
                players={matchAnalysis?.utility?.players || []}
                emptyTitle="Utility behavior data is still pending."
                emptyText="This section shows per-player used, saved and wasted purchases alongside enemy and friendly utility damage once a match economy summary with behavior data has been uploaded."
              />
            </div>

            {shouldShowMatchPageBackToLobby(matchData) ? (
              <div className={classes.backToLobbyRow}>
                <Button
                  to="/lobby"
                  component={Link}
                  variant="contained"
                  className={classes.btnBackToLobby}
                >
                  Back to Lobby
                </Button>
              </div>
            ) : null}

            <div className={classes.vspacer10} />

            <ReportDialog
              openReportDialog={this.state.openReportDialog}
              onClose={this.closeReportDialog}
              steamId={this.state.reportTarget?.steamId}
              nickname={this.state.reportTarget?.nickname}
              matchId={currentMatchId}
              actions={this.props.actions}
              onSubmitted={this.handleReportSubmitted}
            />
          </>
        </Paper>
      </div>
    );
  }
}

MatchPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
  match: PropTypes.object,
};

export default withStyles(styles)(MatchPage);
